export type SetupStatusTone = 'neutral' | 'progress' | 'success';

export type SetupStatusSummary = {
  label: string;
  tone: SetupStatusTone;
};

type LoadableSetupInput = {
  loading?: boolean;
};

type HomeSetupStatusInput = LoadableSetupInput & {
  address?: string | null;
  tenure?: string | null;
  councilTaxBand?: string | null;
  vehicleRegistrationNumber?: string | null;
  vehicleMotDueDate?: string | null;
  vehicleTaxDueDate?: string | null;
};

type MoneySetupStatusInput = LoadableSetupInput & {
  connectedAccountCount?: number | null;
};

type StorageCalendarSetupStatusInput = LoadableSetupInput & {
  documentsMode?: string | null;
  calendarMode?: string | null;
};

type AccountSetupStatusInput = LoadableSetupInput & {
  pushNotificationsEnabled?: boolean | null;
  defaultSection?: string | null;
};

const LOADING_STATUS: SetupStatusSummary = {
  label: 'Loading…',
  tone: 'neutral',
};

function hasValue(value: string | null | undefined) {
  return Boolean(value?.trim());
}

function isExternalDocumentsMode(value: string | null | undefined) {
  return value === 'google_drive' || value === 'icloud';
}

function isExternalCalendarMode(value: string | null | undefined) {
  return value === 'google_calendar' || value === 'icloud';
}

export function buildHomeSetupStatus(input: HomeSetupStatusInput | null | undefined): SetupStatusSummary {
  if (input?.loading) {
    return LOADING_STATUS;
  }

  const hasCoreHomeSetup = hasValue(input?.address) && hasValue(input?.tenure) && hasValue(input?.councilTaxBand);
  const hasAnyHomeSetup = [
    input?.address,
    input?.tenure,
    input?.councilTaxBand,
    input?.vehicleRegistrationNumber,
    input?.vehicleMotDueDate,
    input?.vehicleTaxDueDate,
  ].some(hasValue);

  if (hasCoreHomeSetup) {
    return {
      label: 'Ready',
      tone: 'success',
    };
  }

  if (hasAnyHomeSetup) {
    return {
      label: 'Partly set up',
      tone: 'progress',
    };
  }

  return {
    label: 'Start here',
    tone: 'neutral',
  };
}

export function buildMoneySetupStatus(input: MoneySetupStatusInput | null | undefined): SetupStatusSummary {
  if (input?.loading) {
    return LOADING_STATUS;
  }

  if ((input?.connectedAccountCount || 0) > 0) {
    return {
      label: 'Connected',
      tone: 'success',
    };
  }

  return {
    label: 'Optional',
    tone: 'neutral',
  };
}

export function buildStorageCalendarSetupStatus(input: StorageCalendarSetupStatusInput | null | undefined): SetupStatusSummary {
  if (input?.loading) {
    return LOADING_STATUS;
  }

  const documentsConnected = isExternalDocumentsMode(input?.documentsMode);
  const calendarConnected = isExternalCalendarMode(input?.calendarMode);

  if (documentsConnected && calendarConnected) {
    return {
      label: 'Connected',
      tone: 'success',
    };
  }

  if (documentsConnected || calendarConnected) {
    return {
      label: 'Partly connected',
      tone: 'progress',
    };
  }

  return {
    label: 'Using Adulting',
    tone: 'neutral',
  };
}

export function buildAccountSetupStatus(input: AccountSetupStatusInput | null | undefined): SetupStatusSummary {
  if (input?.loading) {
    return LOADING_STATUS;
  }

  const hasCustomStartSection = hasValue(input?.defaultSection);
  const hasPushEnabled = Boolean(input?.pushNotificationsEnabled);

  if (hasCustomStartSection && hasPushEnabled) {
    return {
      label: 'Personalised',
      tone: 'success',
    };
  }

  if (hasCustomStartSection || hasPushEnabled) {
    return {
      label: 'Partly set up',
      tone: 'progress',
    };
  }

  return {
    label: 'Using defaults',
    tone: 'neutral',
  };
}
